import React, { useState, useEffect, useRef, useMemo } from 'react';
import { usePlayer } from '../../context/PlayerContext';
import { useStats } from '../../hooks/useStats';
import StatCard from '../shared/StatCard';
import { Clipboard, BarChart3, TrendingUp, TrendingDown, Award, HelpCircle, RefreshCw, Filter } from 'lucide-react';
import { formatBattingStat, calculateCompleteBattingStats } from '../../utils/calculations';
import { filterGames, calculateFilteredStats } from '../../utils/statsFilters';

const BattingStats = ({ games = [], filters = {} }) => {
  const { playerData, activeSeason, viewingAllSeasons, selectedPlayerId, dataVersion } = usePlayer();
  const [showTooltip, setShowTooltip] = useState(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // Use the useStats hook to get batting stats
  const {
    battingStats: hookBattingStats,
    loading,
    error,
    dataSource,
    statsContext,
    diagnosticInfo,
    refreshStats,
    toggleDebugMode,
    validationResults,
    debugMode
  } = useStats(selectedPlayerId, activeSeason, viewingAllSeasons, dataVersion, playerData);

  // Filter games based on filter criteria
  const filteredGames = useMemo(() => {
    return filterGames(games, filters);
  }, [games, filters]);

  // Calculate stats from filtered games if filters are applied
  const battingStats = useMemo(() => {
    // Check if any filters are applied (except dateRange='all' which is the default)
    const hasActiveFilters = 
      filters.gameId || 
      filters.dateRange !== 'all' || 
      filters.opponent !== 'all' || 
      filters.location !== 'all';

    if (hasActiveFilters && filteredGames.length > 0) {
      // Calculate stats from filtered games
      const calculateBattingStatsFromGames = (games) => {
        // Extract batting stats from each game
        const battingData = games.reduce((acc, game) => {
          if (game.battingStats) {
            // Add each stat from the game to the accumulator
            Object.entries(game.battingStats).forEach(([key, value]) => {
              acc[key] = (acc[key] || 0) + Number(value || 0);
            });
          }
          return acc;
        }, {});

        // Calculate derived stats (avg, obp, slg, etc.)
        return calculateCompleteBattingStats(battingData);
      };

      return calculateFilteredStats(
        filteredGames, 
        calculateBattingStatsFromGames, 
        hookBattingStats
      );
    }

    // If no filters are applied, use the stats from the hook
    return hookBattingStats;
  }, [hookBattingStats, filteredGames, filters]);

  // Show active filter indicator
  const hasActiveFilters = 
    filters.gameId || 
    filters.dateRange !== 'all' || 
    filters.opponent !== 'all' || 
    filters.location !== 'all';
  
  // Log the stats being used
  console.log('BattingStats using stats:', {
    usingSeasonStats: !viewingAllSeasons && activeSeason && playerData?.seasonStats,
    activeSeason: activeSeason?.name,
    viewingAllSeasons,
    battingStats: {
      games: battingStats?.games || 0,
      atBats: battingStats?.atBats || 0,
      hits: battingStats?.hits || 0,
      average: battingStats?.average || 0
    }
  });
  
  // Helper to toggle debug info display
  const handleToggleDebugInfo = () => {
    setShowDebugInfo(prev => !prev);
    toggleDebugMode();
  };
  
  // Format average for display
  const formatAvg = (value) => {
    return formatBattingStat(value);
  };
  
  // Utility to calculate mock trend data
  const calculateTrend = (stat) => {
    // In a real app, this would compare recent performance to past averages
    // For demo purposes, we'll generate a random trend between -25 and 25
    const trend = Math.floor(Math.random() * 51) - 25;
    return trend;
  };
  
  // Trend explanations
  const getTrendExplanation = (stat, trend) => {
    const direction = trend > 0 ? 'up' : 'down';
    const games = '5'; // In a real app, this would be dynamic
    
    switch(stat) {
      case 'avg':
        return `${Math.abs(trend)}% ${direction} from last ${games} games`;
      case 'obp':
        return `On-base percentage is ${direction} ${Math.abs(trend)}% compared to season average`;
      case 'slg':
        return `Slugging percentage is ${direction} ${Math.abs(trend)}% from previous month`;
      case 'ops':
        return `OPS is ${direction} ${Math.abs(trend)}% from your career average`;
      case 'k_rate':
        return `Strikeout rate is ${direction} ${Math.abs(trend)}% from last ${games} games`;
      default:
        return `${direction} ${Math.abs(trend)}% from average`;
    }
  };
  
  // Determine if a trend is good or bad
  const isTrendPositive = (stat, trend) => {
    // For most batting stats, up is good
    if (stat === 'k_rate') {
      // For strikeout rate, down is good
      return trend < 0;
    }
    // For all other batting stats, up is good
    return trend > 0;
  };
  
  // Tooltip content for stats
  const tooltips = {
    obp: "On-base percentage: (Hits + Walks + HBP) / (At Bats + Walks + HBP). Measures how often a batter reaches base.",
    slg: "Slugging percentage: Total bases / At Bats. Singles = 1, Doubles = 2, Triples = 3, Home Runs = 4. Measures batting power.",
    ops: "On-base plus slugging: OBP + SLG. Combines how often a player gets on base with how many bases they get per at-bat.",
    hbp: "Hit by pitch: Times hit by a pitched ball, counts toward on-base percentage.",
    average: "Batting average: Hits / At Bats. The fundamental measure of batting success.",
    totalBases: "Total bases: Singles + (2 × Doubles) + (3 × Triples) + (4 × Home Runs). Used to calculate slugging percentage."
  };
  
  // Tooltip component
  const Tooltip = ({ id, content }) => (
    <div 
      className="absolute z-10 bg-gray-800 dark:bg-dark-400 text-white p-2 rounded shadow-lg text-xs max-w-xs"
      style={{ bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '8px' }}
    >
      {content}
      <div 
        className="absolute w-2 h-2 bg-gray-800 dark:bg-dark-400 transform rotate-45"
        style={{ bottom: '-4px', left: '50%', marginLeft: '-4px' }}
      ></div>
    </div>
  );

  // Stat item with tooltip support
  const StatItem = ({ label, value, tooltip }) => (
    <div className="relative flex justify-between items-center mb-2 pb-2 border-b border-gray-100 dark:border-dark-300">
      <div className="flex items-center">
        <span>{label}</span>
        {tooltip && (
          <div className="relative ml-1">
            <HelpCircle 
              size={14} 
              className="text-gray-400 dark:text-gray-500 cursor-help" 
              onMouseEnter={() => setShowTooltip(label)} 
              onMouseLeave={() => setShowTooltip(null)} 
            />
            {showTooltip === label && <Tooltip id={label} content={tooltip} />}
          </div>
        )}
      </div>
      <span className="font-semibold">{value}</span>
    </div>
  );

  // Stat item with trend indicator
  const StatItemWithTrend = ({ label, value, tooltip, statKey }) => {
    const trend = calculateTrend(statKey);
    const isPositive = isTrendPositive(statKey, trend);
    const trendExplanation = getTrendExplanation(statKey, trend);

    return (
      <div className="relative flex justify-between items-center mb-2 pb-2 border-b border-gray-100 dark:border-dark-300">
        <div className="flex items-center">
          <span>{label}</span>
          {tooltip && (
            <div className="relative ml-1">
              <HelpCircle 
                size={14} 
                className="text-gray-400 dark:text-gray-500 cursor-help" 
                onMouseEnter={() => setShowTooltip(label)} 
                onMouseLeave={() => setShowTooltip(null)} 
              />
              {showTooltip === label && <Tooltip id={label} content={tooltip} />}
            </div>
          )}
        </div>
        <div className="flex items-center">
          <div 
            className="relative mr-2 cursor-help"
            onMouseEnter={() => setShowTooltip(`trend-${statKey}`)} 
            onMouseLeave={() => setShowTooltip(null)}
          >
            {isPositive ? (
              <TrendingUp size={16} className="text-green-500 dark:text-green-400" />
            ) : (
              <TrendingDown size={16} className="text-red-500 dark:text-red-400" />
            )}
            {showTooltip === `trend-${statKey}` && (
              <Tooltip id={`trend-${statKey}`} content={trendExplanation} />
            )}
          </div>
          <span className="font-semibold">{value}</span>
        </div>
      </div>
    );
  };

  // Add debounce for stats refresh
  const refreshTimerRef = useRef(null);
  
  // Effect to fetch stats on mount and when dependencies change, with debounce
  useEffect(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    
    // Set a new timer to delay the refresh
    refreshTimerRef.current = setTimeout(() => {
      console.log('Stats refresh triggered due to dependency change (debounced):', {
        playerId: selectedPlayerId,
        seasonId: activeSeason?.id,
        viewingAllSeasons
      });
      
      // Use the cached stats if available instead of forcing a refresh
      // This prevents too many Firestore requests
    }, 1000); // 1 second debounce
    
    // Cleanup function
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [selectedPlayerId, activeSeason, viewingAllSeasons]);
  
  // Handle manual refresh click
  const handleRefreshClick = () => {
    refreshStats();
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <h2 className="text-xl font-bold dark:text-gray-100">Batting Statistics</h2>
          
          {/* Show filter indicator if filters are applied */}
          {hasActiveFilters && (
            <div className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full flex items-center">
              <Filter size={12} className="mr-1" />
              <span>Filtered</span>
            </div>
          )}
        </div>
        
        {/* Refresh Button */}
        <button 
          onClick={handleRefreshClick} 
          className="flex items-center px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:hover:bg-blue-800/40 dark:text-blue-300 rounded-md transition-colors"
          disabled={loading}
          title="Refresh statistics"
        >
          <RefreshCw size={16} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          <span className="text-sm">Refresh Stats</span>
        </button>
      </div>
      
      {/* Loading indicator */}
      {loading && (
        <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 border border-blue-100 dark:border-blue-800/30 rounded-md text-sm">
          <div className="flex items-center">
            <RefreshCw size={14} className="animate-spin mr-2" />
            <span>Refreshing statistics...</span>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Overview Card */}
        <StatCard
          title="Overview"
          icon={Clipboard}
          className="h-full"
        >
          <div className="space-y-1">
            <StatItem 
              label="Games" 
              value={battingStats.games} 
            />
            <StatItem 
              label="At Bats (AB)" 
              value={battingStats.atBats} 
            />
            <StatItem 
              label="Hits (H)" 
              value={battingStats.hits} 
            />
            <StatItemWithTrend 
              label="Batting Average (AVG)" 
              value={formatAvg(battingStats.average)} 
              statKey="avg"
              tooltip={tooltips.average}
            />
          </div>
        </StatCard>
        
        {/* Power Numbers Card */}
        <StatCard
          title="Power Numbers"
          icon={Award}
          className="h-full"
        >
          <div className="space-y-1">
            <StatItem 
              label="Doubles (2B)" 
              value={battingStats.doubles} 
            />
            <StatItem 
              label="Triples (3B)" 
              value={battingStats.triples} 
            />
            <StatItem 
              label="Home Runs (HR)" 
              value={battingStats.homeRuns} 
            />
            <StatItem 
              label="Runs Scored (R)" 
              value={battingStats.runs} 
            />
            <StatItem 
              label="Runs Batted In (RBI)" 
              value={battingStats.rbi} 
            />
            <StatItem 
              label="Total Bases (TB)" 
              value={battingStats.totalBases} 
              tooltip={tooltips.totalBases}
            />
          </div>
        </StatCard>
        
        {/* Advanced Stats Card */}
        <StatCard
          title="Advanced Stats"
          icon={TrendingUp}
          className="h-full"
        >
          <div className="space-y-1">
            <StatItemWithTrend 
              label="On-Base Percentage (OBP)" 
              value={formatAvg(battingStats.obp)} 
              tooltip={tooltips.obp}
              statKey="obp"
            />
            <StatItemWithTrend 
              label="Slugging (SLG)" 
              value={formatAvg(battingStats.slg)} 
              tooltip={tooltips.slg}
              statKey="slg"
            />
            <StatItemWithTrend 
              label="OPS" 
              value={formatAvg(battingStats.ops)} 
              tooltip={tooltips.ops}
              statKey="ops"
            />
          </div>
        </StatCard>
        
        {/* Additional Stats Card */}
        <StatCard
          title="Additional Stats"
          icon={BarChart3}
          className="h-full"
        >
          <div className="space-y-1">
            <StatItemWithTrend 
              label="Strikeouts (K)" 
              value={battingStats.strikeouts}
              statKey="k_rate" 
            />
            <StatItem 
              label="Hit By Pitch (HBP)" 
              value={battingStats.hitByPitch} 
              tooltip={tooltips.hbp}
            />
            <StatItem 
              label="Walks (BB)" 
              value={battingStats.walks} 
            />
            {battingStats.atBats > 0 && (
              <StatItem 
                label="K Rate" 
                value={`${((battingStats.strikeouts / battingStats.atBats) * 100).toFixed(1)}%`} 
                tooltip="Strikeout Rate: Percentage of at-bats that end in a strikeout"
              />
            )}
          </div>
        </StatCard>
        
      {/* Display stats context information */}
      {(activeSeason || hasActiveFilters) && (
        <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 border border-blue-100 dark:border-blue-800/30 rounded-md text-sm">
          <div className="flex items-center">
            {activeSeason && !viewingAllSeasons && (
              <>
                <span className="mr-1">Season:</span>
                <span className="font-medium">{activeSeason.year} {activeSeason.name}</span>
              </>
            )}
            
            {hasActiveFilters && (
              <div className="ml-auto flex items-center">
                <Filter size={14} className="mr-1" />
                <span>
                  Showing {filteredGames.length} of {games.length} games
                </span>
              </div>
            )}
          </div>
          <div className="text-xs mt-1 text-blue-500 dark:text-blue-400">
            {battingStats.games} game{battingStats.games !== 1 ? 's' : ''} played
          </div>
        </div>
      )}
        
        {/* Display message if no stats available */}
        {battingStats.games === 0 && (
          <div className="col-span-2 p-6 text-center bg-gray-50 dark:bg-dark-300 rounded-lg border border-gray-200 dark:border-dark-400">
            <p className="text-gray-600 dark:text-darkText-secondary mb-2">No batting statistics available</p>
            <p className="text-sm text-gray-500 dark:text-darkText-tertiary">
              {activeSeason && !viewingAllSeasons 
                ? `No batting data recorded for the ${activeSeason.name} season.` 
                : "No career batting statistics available."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BattingStats;
